import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Loader2, Plus, RefreshCcw, RotateCw, Send, Settings2, ListChecks, History } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';
import { CATEGORY, dmy, errText } from '../Settings/payRatesKit';
import { money } from '../../utils/format';

// Timesheets > Xero payroll for a company paid from Pay Rates (owner spec v1.2, section 7). Each of
// the eight pay line categories maps to its own Xero pay item paid per unit; Rostery writes each
// employee's rates into their Xero pay template, then sends one draft timesheet per employee per pay
// period from the approved pay lines, with any back pay as dollar units on the Back Pay pay item.
// Backend: routes/xeroPayrollRates.js, lib/xeroPayroll.js.

const CATS = ['weekday', 'weekday_evening', 'saturday', 'sunday', 'public_holiday', 'sleepover', 'km', 'back_pay'];
const LABEL = { ...Object.fromEntries(CATS.map((c) => [c, (CATEGORY[c] && CATEGORY[c].label) || c])), back_pay: 'Back Pay' };
const UNIT = { weekday: 'hours', weekday_evening: 'hours', saturday: 'hours', sunday: 'hours', public_holiday: 'hours', sleepover: 'sleepovers', km: 'km', back_pay: 'dollars, rate fixed at $1.00' };
const STATUS = {
  ready: { tone: 'success', label: 'Ready' },
  resend: { tone: 'warning', label: 'Changed: send again' },
  error: { tone: 'danger', label: 'Xero refused it' },
  sent: { tone: 'info', label: 'In Xero (draft)' },
  approved: { tone: 'info', label: 'Approved in Xero' },
  processed: { tone: 'neutral', label: 'Processed in Xero' },
  needs_mapping: { tone: 'warning', label: 'Pay item missing' },
  dry_run_required: { tone: 'warning', label: 'Dry run first' },
  before_cutover: { tone: 'warning', label: 'Before Pay Rates' },
  unapproved: { tone: 'warning', label: 'Unapproved shifts' },
  not_linked: { tone: 'neutral', label: 'Not linked' },
  not_in_xero: { tone: 'warning', label: 'Not in Xero' },
  other_calendar: { tone: 'neutral', label: 'Other pay calendar' },
  in_xero: { tone: 'warning', label: 'Already in Xero' },
  no_lines: { tone: 'neutral', label: 'Nothing to send' },
};
const SENDABLE = new Set(['ready', 'resend', 'error']);
const SOURCE = { in_period_rate_diff: 'Rate change inside the period', backdated_rate: 'Back-dated rate', after_send_change: 'Changed after sending' };
const n2 = (v) => (v ? Number(v).toFixed(2).replace(/\.00$/, '') : '');
const EARN_LABEL = {
  ORDINARYTIMEEARNINGS: 'Ordinary time earnings', OVERTIMEEARNINGS: 'Overtime earnings', ALLOWANCE: 'Allowance',
  BONUSESANDCOMMISSIONS: 'Bonuses and commissions', LUMPSUMD: 'Lump sum D', LUMPSUME: 'Lump sum E (back pay over $1,200 for earlier years)',
  LUMPSUMW: 'Lump sum W', DIRECTORSFEES: "Directors' fees", PAIDPARENTALLEAVE: 'Paid parental leave', WORKERSCOMPENSATION: "Workers' compensation",
};
const typeLabel = (t) => EARN_LABEL[t] || String(t || '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

function CreateItemForm({ category, setup, onDone, onCancel }) {
  const start = (setup.categories.find((c) => c.key === category) || {}).starting || {};
  const wages = (setup.accounts || []).find((a) => /wage|salar/i.test(a.name)) || (setup.accounts || [])[0];
  const [f, setF] = useState({ name: start.name || '', earningsType: start.earningsType || 'ORDINARYTIMEEARNINGS', allowanceType: start.allowanceType || 'OTHER', units: start.units || '', accountCode: wages ? wages.code : '', qualifying: start.qualifying !== false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/xero-sync/rates/pay-items', { category, ...f });
      toast.success(`${data.item.name} created in Xero and mapped to ${LABEL[category]}`);
      onDone(data);
    } catch (e) { toast.error(errText(e, 'Xero did not create the pay item')); setBusy(false); }
  };
  const id = (k) => `xpc-${category}-${k}`;
  return (
    <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-2">
      <p className="text-[12px] text-slate-600">Starting values for a new Xero pay item paid per unit. Change any of them first if you need to.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div><label htmlFor={id('name')} className="text-[12px] text-slate-600 block">Name</label><input id={id('name')} className="input-field" value={f.name} onChange={set('name')} /></div>
        <div><label htmlFor={id('units')} className="text-[12px] text-slate-600 block">Units</label><input id={id('units')} className="input-field" value={f.units} onChange={set('units')} /></div>
        <div>
          <label htmlFor={id('type')} className="text-[12px] text-slate-600 block">Earnings type</label>
          <select id={id('type')} className="input-field" value={f.earningsType} onChange={set('earningsType')}>
            {setup.earningsTypes.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </select>
        </div>
        {f.earningsType === 'ALLOWANCE' && (
          <div>
            <label htmlFor={id('allow')} className="text-[12px] text-slate-600 block">Allowance type</label>
            <select id={id('allow')} className="input-field" value={f.allowanceType} onChange={set('allowanceType')}>
              {setup.allowanceTypes.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
            </select>
          </div>
        )}
        <div>
          <label htmlFor={id('acct')} className="text-[12px] text-slate-600 block">Xero account</label>
          <select id={id('acct')} className="input-field" value={f.accountCode} onChange={set('accountCode')}>
            <option value="">Choose…</option>
            {(setup.accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
          </select>
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
        <input type="checkbox" checked={f.qualifying} onChange={set('qualifying')} /> Counts as qualifying earnings for super
      </label>
      {category === 'back_pay' && <p className="text-[12px] text-slate-500">Rate per unit is fixed at $1.00, so the units are dollars.</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary btn-sm" onClick={save} disabled={busy || !f.accountCode}>
          {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Create in Xero
        </button>
      </div>
    </div>
  );
}

function MappingPanel({ setup, onSaved }) {
  const [map, setMap] = useState(setup.settings.map);
  const [creating, setCreating] = useState('');
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState(setup.ratePerUnitRates);
  useEffect(() => { setMap(setup.settings.map); setRates(setup.ratePerUnitRates); }, [setup]);
  const missing = CATS.filter((c) => !map[c]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put('/xero-sync/rates/mapping', { map });
      toast.success(data.sync && data.sync.synced ? `Mapping saved. Rates synced to ${plural(data.sync.synced, 'employee')}.` : 'Pay item mapping saved');
      onSaved();
    } catch (e) { toast.error(errText(e, 'Could not save the mapping')); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-800">Pay item mapping</h4>
        <p className="text-[12.5px] text-slate-500 max-w-[75ch]">Each category needs its own Xero pay item set to Rate per unit, because the rate comes from the employee's pay template. All eight must be mapped before the first send.</p>
      </div>
      {missing.length > 0 && <p className="text-[13px] text-amber-700">Still to map: {missing.map((c) => LABEL[c]).join(', ')}.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2">
        {CATS.map((c) => (
          <div key={c}>
            <div className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2">
              <label htmlFor={`xpm-${c}`} className="text-[13px] text-slate-700">{LABEL[c]}<span className="block text-[11px] text-slate-400">{UNIT[c]}</span></label>
              <select id={`xpm-${c}`} className="input-field" value={map[c] || ''} onChange={(e) => setMap((m) => ({ ...m, [c]: e.target.value || null }))}>
                <option value="">Not mapped</option>
                {rates.map((r) => <option key={r.id} value={r.id}>{r.name}{r.units ? ` (${r.units})` : ''}</option>)}
              </select>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setCreating(creating === c ? '' : c)} aria-expanded={creating === c} title={`Create a ${LABEL[c]} pay item in Xero`}>
                <Plus size={13} aria-hidden="true" /> Create
              </button>
            </div>
            {creating === c && (
              <CreateItemForm category={c} setup={setup} onCancel={() => setCreating('')}
                onDone={(d) => { setCreating(''); setRates(d.ratePerUnitRates); setMap(d.settings.map); onSaved(); }} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={save}>
          {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Save mapping
        </button>
      </div>
    </div>
  );
}

function RateSyncPanel({ setup, canSend, onChanged }) {
  const confirm = useConfirm();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState('');
  const [log, setLog] = useState(null);
  const viewed = !!setup.settings.dryRunViewedAt || !!plan;
  const dryRun = async () => {
    setBusy('dry');
    try { const { data } = await api.get('/xero-sync/rates/sync/dry-run'); setPlan(data); onChanged(); } catch (e) { toast.error(errText(e, 'Could not work out the rate sync')); }
    finally { setBusy(''); }
  };
  const sync = async () => {
    const changes = plan ? plan.results.reduce((a, r) => a + (r.changes || []).length, 0) : null;
    const ok = await confirm(changes != null ? `${plural(changes, 'pay template line')} will be written to Xero, as listed in the dry run. Every other line in each pay template stays as it is.` : 'Each linked employee\'s Xero pay template gets their Rostery rates on the mapped pay items. Every other line stays as it is.',
      { title: 'Sync rates to Xero?', confirmLabel: 'Sync rates' });
    if (!ok) return;
    setBusy('sync');
    try {
      const { data } = await api.post('/xero-sync/rates/sync', {});
      if (data.synced) toast.success(`Rates synced for ${plural(data.synced, 'employee')}`);
      if (data.failed) toast.error(`${plural(data.failed, 'employee')} failed: ${(data.results.find((r) => !r.ok) || {}).error || ''}`, { duration: 9000 });
      setPlan(null); onChanged();
      if (log) loadLog();
    } catch (e) { toast.error(errText(e, 'The rate sync failed')); }
    finally { setBusy(''); }
  };
  const loadLog = async () => {
    try { const { data } = await api.get('/xero-sync/rates/sync/log?limit=100'); setLog(data.log); } catch (e) { toast.error(errText(e, 'Could not read the sync log')); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <h4 className="text-sm font-semibold text-slate-800">Rate sync to Xero pay templates</h4>
          <p className="text-[12.5px] text-slate-500 max-w-[75ch]">Rostery is the source of truth. Each employee's mapped pay template lines carry their rate on the start date of the pay period being paid, and Back Pay is $1.00. A rate changed in Xero is put back on the next sync. Look at the dry run before the first sync.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary btn-sm" onClick={dryRun} disabled={!!busy}>
            {busy === 'dry' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Eye size={13} aria-hidden="true" />} Dry run
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={sync} disabled={!!busy || !viewed} title={viewed ? undefined : 'Look at the dry run first'}>
            {busy === 'sync' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <RotateCw size={13} aria-hidden="true" />} Sync rates now
          </button>
          {canSend && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => (log ? setLog(null) : loadLog())} aria-expanded={!!log}>
              <History size={13} aria-hidden="true" /> {log ? 'Hide sync log' : 'Sync log'}
            </button>
          )}
        </div>
      </div>
      {plan && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
              <tr><th className="px-3 py-2 font-medium">Employee</th><th className="px-3 py-2 font-medium">Pay period from</th><th className="px-3 py-2 font-medium">What would change in Xero</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plan.results.length === 0 && <tr><td colSpan={3} className="px-3 py-3 text-slate-500">No employee is linked to Xero yet. Link them on their profile, Pay Rates.</td></tr>}
              {plan.results.map((r) => (
                <tr key={r.userId} className="align-top">
                  <td className="px-3 py-2"><p className="font-medium text-slate-800">{r.name}</p>{r.xeroName && r.xeroName !== r.name && <p className="text-[12px] text-slate-500">Xero: {r.xeroName}</p>}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{r.periodStart ? dmy(r.periodStart) : ''}</td>
                  <td className="px-3 py-2">
                    {!r.ok ? <p className="text-rose-700">{r.error}</p>
                      : !(r.changes || []).length ? <p className="text-slate-500">Already in sync</p>
                      : (
                        <ul className="space-y-0.5">
                          {r.changes.map((c) => (
                            <li key={c.category}>{LABEL[c.category]}{c.payItem ? ` (${c.payItem})` : ''}: {c.action === 'add' ? 'add the line at ' : `${c.old == null ? 'pay item rate' : money(c.old)} to `}<span className="font-semibold">{money(c.new)}</span></li>
                          ))}
                        </ul>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {log && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-80">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-left text-[11.5px] text-slate-500 sticky top-0">
              <tr><th className="px-3 py-2">Time</th><th className="px-3 py-2">Employee</th><th className="px-3 py-2">Pay item</th><th className="px-3 py-2 text-right">Old Xero rate</th><th className="px-3 py-2 text-right">New rate</th><th className="px-3 py-2">Result</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {log.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-slate-500">Nothing synced yet.</td></tr>}
              {log.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{dmy(l.created_at)} {String(l.created_at || '').slice(11, 16)}</td>
                  <td className="px-3 py-1.5">{l.name}</td>
                  <td className="px-3 py-1.5">{l.label ? `${l.label}${l.pay_item_name ? ` (${l.pay_item_name})` : ''}` : (l.action === 'checked' ? 'All mapped lines' : '')}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{l.old_rate != null ? money(l.old_rate) : ''}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{l.new_rate != null ? money(l.new_rate) : ''}</td>
                  <td className="px-3 py-1.5">{l.ok ? <span className="text-emerald-700">{l.action === 'checked' ? 'Already in sync' : 'Saved'}</span> : <span className="text-rose-700">{l.error}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SendSettingsPanel({ setup, onSaved }) {
  const [calendarId, setCalendarId] = useState(setup.settings.calendarId || '');
  const [autoSend, setAutoSend] = useState(!!setup.settings.autoSend);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put('/xero-sync/rates/settings', { calendarId: calendarId || null, autoSend }); toast.success('Send settings saved'); onSaved(); }
    catch (e) { toast.error(errText(e, 'Could not save the send settings')); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <h4 className="text-sm font-semibold text-slate-800">Send settings</h4>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="xps-cal" className="text-[13px] text-slate-700 block mb-1">Payroll calendar</label>
          <select id="xps-cal" className="input-field" value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
            <option value="">Choose…</option>
            {setup.calendars.filter((c) => c.supported).map((c) => <option key={c.id} value={c.id}>{c.name} ({String(c.type).toLowerCase()})</option>)}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
          <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
          Send automatically once every shift in the period is approved
        </label>
        <button type="button" className="btn-primary btn-sm mb-1" disabled={busy || (autoSend && !calendarId)} onClick={save}>
          {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Save
        </button>
      </div>
      <p className="text-[12px] text-slate-500">Either way, timesheets arrive in Xero as drafts and Rostery never runs a pay run. Rostery checks Xero every hour: a processed timesheet locks its shifts, and a draft whose shifts changed is sent again.</p>
    </div>
  );
}

export default function XeroRatesPayroll() {
  const confirm = useConfirm();
  const { can } = usePayPermissions();
  const canSend = can('payroll.send_xero');
  const canSetup = can('integrations.xero');
  const [setup, setSetup] = useState(null);
  const [setupError, setSetupError] = useState(null);
  const [panel, setPanel] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState('');

  const loadSetup = useCallback(async () => {
    setSetupError(null);
    try {
      const { data: st } = await api.get('/xero-sync/status').catch(() => ({ data: { connected: false } }));
      if (!st?.connected) { setSetupError({ code: 'XERO_NOT_CONNECTED' }); return; }
      const { data } = await api.get('/xero-sync/rates/setup');
      setSetup(data);
      const cals = data.calendars.filter((c) => c.supported);
      const cal = cals.find((c) => c.id === data.settings.calendarId) || cals[0];
      if (cal) {
        setCalendarId((cur) => cur || cal.id);
        const usable = cal.periods.filter((p) => p.end >= data.cutover_date);
        const p = usable.find((x) => x.ended) || usable[usable.length - 1];
        setPeriodStart((cur) => cur || (p && p.start) || '');
      }
      if (data.settings.missing.length && canSetup) setPanel((v) => v || 'mapping');
    } catch (e) { setSetupError(e.response?.data || { message: 'Could not read your Xero payroll set-up.' }); }
  }, [canSetup]);
  useEffect(() => { loadSetup(); }, [loadSetup]);

  const calendar = useMemo(() => (setup?.calendars || []).find((c) => c.id === calendarId), [setup, calendarId]);
  const periods = useMemo(() => (calendar?.periods || []).filter((p) => !setup || p.end >= setup.cutover_date), [calendar, setup]);

  const load = useCallback(async () => {
    if (!calendarId || !periodStart) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/xero-sync/rates/period?calendarId=${calendarId}&periodStart=${periodStart}`);
      setView(data);
      setPicked(new Set(data.rows.filter((r) => r.status === 'ready' || r.status === 'resend').map((r) => r.userId)));
    } catch (e) { setError(errText(e, 'Could not prepare the pay period.')); }
    finally { setLoading(false); }
  }, [calendarId, periodStart]);
  useEffect(() => { if (setup && !setup.needsReconnect) load(); }, [setup, load]);

  const rows = view?.rows || [];
  const sendable = rows.filter((r) => SENDABLE.has(r.status));
  const chosen = sendable.filter((r) => picked.has(r.userId));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const shownCats = CATS.filter((c) => rows.some((r) => r.totals && r.totals[c]));

  const send = async (list = chosen) => {
    if (!list.length) return;
    const ok = await confirm(`${plural(list.length, 'draft timesheet')} for ${dmy(view.periodStart)} to ${dmy(view.periodEnd)} go to Xero. Each employee's pay template rates are checked first. Then check them in Xero and run the pay run there.`,
      { title: `Send ${plural(list.length, 'timesheet')} to Xero?`, confirmLabel: 'Send to Xero' });
    if (!ok) return;
    setBusy('send');
    try {
      const { data } = await api.post('/xero-sync/rates/send', { calendarId, periodStart, userIds: list.map((r) => r.userId) });
      const good = data.results.filter((r) => r.ok); const bad = data.results.filter((r) => !r.ok);
      if (good.length) toast.success(`${plural(good.length, 'timesheet')} in Xero as drafts`);
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(errText(e, 'Sending to Xero failed')); }
    finally { setBusy(''); }
  };
  const refresh = async () => {
    setBusy('refresh');
    try { const { data } = await api.post('/xero-sync/rates/refresh', { periodStart }); toast.success(`Checked ${plural(data.checked, 'timesheet')} in Xero`); await load(); }
    catch (e) { toast.error(errText(e, 'Could not read the status from Xero')); }
    finally { setBusy(''); }
  };

  if (setupError) {
    const notConnected = setupError.code === 'XERO_NOT_CONNECTED';
    const reconnect = setupError.code === 'XERO_RECONNECT';
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title={notConnected ? 'Xero is not connected' : reconnect ? 'Reconnect Xero' : 'Xero payroll could not be read'}
          hint={notConnected ? 'Connect Xero to send timesheets to it.' : setupError.message}
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }
  if (!setup) return <div className="card"><TableSkeleton rows={4} cols={6} /></div>;
  if (setup.needsReconnect) {
    return (
      <Notice tone="amber" title="Reconnect Xero to send timesheets" actions={<Link to="/settings?tab=integrations" className="btn-secondary btn-sm">Go to integrations</Link>}>
        Rostery needs permission to read your Xero pay calendars, pay items and employees. In Settings, Integrations, press Reconnect on Xero and allow access, then come back here.
      </Notice>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0 font-bold text-[#13B5EA]" aria-hidden="true">X</span>
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-base font-semibold text-slate-900">Send pay lines to Xero payroll</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-[75ch]">
              Approved pay lines for one pay period, as units per pay item per day: hours, sleepovers and km. Xero pays them at the rates Rostery keeps in each employee's pay template, and back pay goes on Back Pay as dollars. One draft timesheet per employee.
            </p>
          </div>
          {canSetup && (
            <div className="flex flex-wrap gap-2">
              {[['mapping', 'Pay item mapping', Settings2], ['sync', 'Rate sync', RotateCw], ['send', 'Send settings', ListChecks]].map(([k, label, Icon]) => (
                <button key={k} type="button" className={`btn-sm ${panel === k ? 'btn-primary' : 'btn-secondary'}`} aria-expanded={panel === k} onClick={() => setPanel(panel === k ? '' : k)}>
                  <Icon size={13} aria-hidden="true" /> {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {setup.settings.missing.length > 0 && !canSetup && (
          <p className="text-[13px] text-amber-700">Not every category is mapped to a Xero pay item yet. Someone with the Integrations: Xero permission needs to finish the mapping first.</p>
        )}
        {panel === 'mapping' && canSetup && <MappingPanel setup={setup} onSaved={loadSetup} />}
        {panel === 'sync' && canSetup && <RateSyncPanel setup={setup} canSend={canSend} onChanged={loadSetup} />}
        {panel === 'send' && canSetup && <SendSettingsPanel setup={setup} onSaved={loadSetup} />}

        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="xpr-cal" className="text-[13px] text-slate-700 font-medium block mb-1">Payroll calendar</label>
            <select id="xpr-cal" className="input-field" value={calendarId} onChange={(e) => { setCalendarId(e.target.value); setPeriodStart(''); setView(null); }}>
              {setup.calendars.map((c) => <option key={c.id} value={c.id} disabled={!c.supported}>{c.name} ({String(c.type || '').toLowerCase()}{c.supported ? '' : ', not supported'})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="xpr-period" className="text-[13px] text-slate-700 font-medium block mb-1">Pay period</label>
            <select id="xpr-period" className="input-field" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}>
              <option value="">Choose…</option>
              {periods.map((p) => <option key={p.start} value={p.start}>{dmy(p.start)} to {dmy(p.end)}{p.current ? ' (current)' : ''}</option>)}
            </select>
          </div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading || !periodStart}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Open period
          </button>
          {canSend && view && (
            <button type="button" className="btn-ghost" onClick={refresh} disabled={!!busy}>
              {busy === 'refresh' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RotateCw size={14} aria-hidden="true" />} Check Xero now
            </button>
          )}
        </div>
        <p className="text-[12px] text-slate-500">Pay Rates started on {dmy(setup.cutover_date)}. Earlier pay periods were paid from pay groups.</p>
      </div>

      {view && view.warnings && view.warnings.length > 0 && <Notice tone="amber" title="Xero status not read">{view.warnings.join(' ')}</Notice>}
      {view && view.exceptions.length > 0 && (
        <Notice tone="amber" title={`Exceptions: ${plural(view.exceptions.length, 'employee')} held back`}>
          <ul className="list-disc pl-4 space-y-0.5">
            {view.exceptions.map((x) => <li key={x.userId}><span className="font-medium">{x.name}</span>: {x.reason}</li>)}
          </ul>
        </Notice>
      )}

      <div className="card overflow-hidden">
        {loading && !view ? <TableSkeleton rows={5} cols={8} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !view ? <EmptyState icon={Send} title="Choose a pay period" hint="Rostery then shows each employee's pay lines for it." />
          : !rows.length ? <EmptyState icon={Send} title="No pay lines in this period" hint="Approve timesheets first." />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10"><span className="sr-only">Send</span></th>
                      <th className="px-3 py-2.5 font-medium">Employee</th>
                      {shownCats.map((c) => <th key={c} className="px-2 py-2.5 font-medium text-right whitespace-nowrap">{LABEL[c]}</th>)}
                      <th className="px-2 py-2.5 font-medium text-right whitespace-nowrap">Back pay</th>
                      <th className="px-3 py-2.5 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => {
                      const st = STATUS[r.status] || STATUS.no_lines;
                      const bp = r.record && ['sent', 'approved', 'processed'].includes(r.status) ? r.record.backPay : r.backPay.estimate;
                      return (
                        <tr key={r.userId} className="align-top">
                          <td className="px-4 py-3"><input type="checkbox" aria-label={`Send ${r.name}`} disabled={!SENDABLE.has(r.status) || !canSend} checked={picked.has(r.userId)} onChange={() => toggle(r.userId)} /></td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-slate-900">{r.name}</p>
                            <p className="text-[12px] text-slate-500">{plural(r.shifts, 'shift')} · {money(r.amount)}{r.xeroName && r.xeroName !== r.name ? ` · Xero: ${r.xeroName}` : ''}</p>
                          </td>
                          {shownCats.map((c) => <td key={c} className="px-2 py-3 text-right tabular-nums text-slate-700">{n2(r.totals[c])}</td>)}
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{bp ? money(bp) : ''}{r.backPay.pending < 0 && <span className="block text-[11px] text-amber-700">{money(r.backPay.pending)} waiting</span>}</td>
                          <td className="px-3 py-3">
                            <StatusPill tone={st.tone} label={st.label} />
                            {r.reason && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{r.reason}</p>}
                            {r.note && <p className="text-[12px] text-sky-700 mt-1 max-w-[18rem]">{r.note}</p>}
                            {r.status === 'error' && canSend && (
                              <button type="button" className="mt-1 text-[12px] font-medium text-sky-700 hover:underline" onClick={() => send([r])} disabled={!!busy}>Send again</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">{dmy(view.periodStart)} to {dmy(view.periodEnd)} · {plural(sendable.length, 'employee')} to send{view.processedInXero ? ` · ${plural(view.processedInXero, 'timesheet')} processed in Xero` : ''}.</p>
                <button type="button" className="btn-primary" disabled={!chosen.length || !!busy || !canSend} onClick={() => send()} title={canSend ? undefined : needsPermission('payroll.send_xero')}>
                  {busy === 'send' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
                  Send {plural(chosen.length, 'timesheet')} to Xero
                </button>
              </div>
            </>
          )}
      </div>

      {view && view.adjustments.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Back pay</h3>
            <p className="text-[12.5px] text-slate-500">Each amount, the shifts it came from, and the old and new rate. A decrease is taken off later back pay; what is left waits until it is used up.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Employee</th><th className="px-3 py-2 font-medium">Why</th><th className="px-3 py-2 font-medium">Shifts</th>
                  <th className="px-3 py-2 font-medium text-right">Old rate</th><th className="px-3 py-2 font-medium text-right">New rate</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th><th className="px-3 py-2 font-medium text-right">This period</th><th className="px-3 py-2 font-medium text-right">Still waiting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {view.adjustments.map((a) => (
                  <tr key={a.id} className="align-top">
                    <td className="px-4 py-2 font-medium text-slate-800">{a.name}</td>
                    <td className="px-3 py-2"><p>{SOURCE[a.source] || a.source}{a.label ? `, ${a.label}` : ''}</p>{a.note && <p className="text-[12px] text-slate-500 max-w-[24rem]">{a.note}</p>}</td>
                    <td className="px-3 py-2 text-slate-600">{a.shifts.length ? a.shifts.map((s) => <span key={s.id} className="block whitespace-nowrap">{dmy(s.date)} {s.start_time} to {s.end_time}{s.clients ? `, ${s.clients}` : ''}</span>) : (a.date ? dmy(a.date) : '')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.old_rate != null ? money(a.old_rate) : ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.new_rate != null ? money(a.new_rate) : ''}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${a.amount < 0 ? 'text-rose-700' : 'text-slate-800'}`}>{money(a.amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.applied_here ? money(a.applied_here) : ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.remaining ? money(a.remaining) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
